import React, { useState, useEffect } from 'react';

import { useTheme } from 'styled-components';

import Select from '~/components/Select';
import StackedColumns from '~/components/StatisticsCharts/StackedColumns';

import { getRepValueForSelect, getClientValueForSelect } from '~/utils';

import { useAuth } from '~/hooks/auth';

import api from '~/services/api';

import { STATE_OPTIONS } from '../../../constants';

interface IFilters {
  state: string;
  state_refreshed: boolean;
  erp_rep_code: string;
  rep_refreshed: boolean;
  erp_client_code: string;
  status: string;
  year: string;
  refresh: boolean;
  refresh_projects: boolean;
}

interface IChartData {
  project_type: string;
  series: { name: string; data: number[] }[];
  categories: string[];
  colors: string[];
}

const PROJECT_TYPES = [
  { name: 'DISPLAY', label: 'Displays' },
  { name: 'MATERIAL', label: 'Materiais' },
  { name: 'PATROCINIO', label: 'Pagamentos' },
  { name: 'ADVERSIDADE', label: 'Problemas' },
  { name: 'PASTA', label: 'Pastas' },
  { name: 'AMOSTRA', label: 'Amostras' },
  { name: 'EVENTO', label: 'Eventos' },
  { name: 'REBATE', label: 'Rebate' },
];

const Procedures: React.FC = () => {
  const { user } = useAuth();
  const { colors } = useTheme();
  const [representatives, setRepresentatives] = useState<
    {
      code: string;
      name: string;
    }[]
  >();
  const [clients, setClients] = useState<
    {
      code: string;
      company_name: string;
    }[]
  >();
  const [projects, setProjects] = useState<
    {
      type: string;
      status: string;
      project_procedure: {
        id: string;
        type: string;
        sequence: number;
        description: string;
        active: boolean;
      };
    }[]
  >([]);
  const [filters, setFilters] = useState<IFilters>({
    state: 'ALL',
    state_refreshed: true,
    erp_rep_code: user.user_type.type === 'REP' ? user.erp_rep_code : 'ALL',
    rep_refreshed: true,
    erp_client_code: 'ALL',
    status: 'ALL',
    year: 'ALL',
    refresh: true,
    refresh_projects: false,
  });
  const [chartData, setChartData] = useState<IChartData[]>([]);

  useEffect(() => {
    const refreshFilters = async () => {
      let { erp_rep_code, erp_client_code, rep_refreshed } = filters;

      if (filters.state_refreshed) {
        await api
          .get(`/localrepresentatives/state?state=${filters.state}`)
          .then(response => {
            setRepresentatives([
              {
                code: 'ALL',
                name: '',
              },
              ...response.data,
            ]);

            erp_rep_code = 'ALL';
            rep_refreshed = true;
          });
      }

      if (rep_refreshed) {
        erp_rep_code =
          user.user_type.type === 'REP' ? user.erp_rep_code : erp_rep_code;

        await api
          .get(`/localclients/rep?rep_code=${erp_rep_code}`)
          .then(response => {
            setClients([
              {
                code: 'ALL',
                company_name: '',
              },
              ...response.data,
            ]);

            erp_client_code = 'ALL';
          });
      }

      setFilters({
        ...filters,
        state_refreshed: false,
        erp_rep_code,
        rep_refreshed: false,
        erp_client_code,
        refresh: false,
        refresh_projects: true,
      });
    };

    if (filters.refresh) {
      refreshFilters();
    }
  }, [filters, user]);

  useEffect(() => {
    const loadProjects = async () => {
      await api
        .get(
          `/projects/projectstatistics?` +
            `state=${filters.state}&` +
            `erp_rep_code=${filters.erp_rep_code}&` +
            `erp_client_code=${filters.erp_client_code}&` +
            `status=${filters.status}&` +
            `year=${filters.year}&` +
            `get_procedures=Y`,
        )
        .then(response => {
          setProjects(response.data);

          setFilters({ ...filters, refresh_projects: false });
        });
    };

    if (filters.refresh_projects) {
      loadProjects();
    }
  }, [filters]);

  useEffect(() => {
    if (projects.length > 0) {
      let projData: {
        type: string;
        procedures: {
          id: string;
          sequence: number;
          description: string;
          pending: number;
          completed: number;
          canceled: number;
        }[];
      }[] = [];

      projects.forEach(project => {
        const pending = project.status === 'Pendente' ? 1 : 0;
        const completed = project.status === 'Finalizado' ? 1 : 0;
        const canceled = project.status === 'Cancelado' ? 1 : 0;

        const findData = projData.find(p_d => p_d.type === project.type);

        if (findData) {
          const findProc = findData.procedures.find(
            proc => proc.id === project.project_procedure.id,
          );

          if (findProc) {
            findProc.pending += pending;
            findProc.completed += completed;
            findProc.canceled += canceled;
          } else {
            findData.procedures = [
              ...findData.procedures,
              {
                id: project.project_procedure.id,
                sequence: project.project_procedure.sequence,
                description: project.project_procedure.description,
                pending,
                completed,
                canceled,
              },
            ];
          }
        } else {
          projData = [
            ...projData,
            {
              type: project.type,
              procedures: [
                {
                  id: project.project_procedure.id,
                  sequence: project.project_procedure.sequence,
                  description: project.project_procedure.description,
                  pending,
                  completed,
                  canceled,
                },
              ],
            },
          ];
        }
      });

      setChartData(
        projData.map(p_d => {
          const categories = p_d.procedures
            .sort((a, b) => a.sequence - b.sequence)
            .map(proc => proc.description);

          const pendingData = p_d.procedures
            .sort((a, b) => a.sequence - b.sequence)
            .map(proc => proc.pending);

          const completedData = p_d.procedures
            .sort((a, b) => a.sequence - b.sequence)
            .map(proc => proc.completed);

          const canceledData = p_d.procedures
            .sort((a, b) => a.sequence - b.sequence)
            .map(proc => proc.canceled);

          return {
            project_type: p_d.type,
            series: [
              { name: 'Pendente', data: pendingData },
              { name: 'Finalizado', data: completedData },
              { name: 'Cancelado', data: canceledData },
            ],
            categories,
            colors: [
              colors.chartPending,
              colors.chartFinished,
              colors.chartCanceled,
            ],
          };
        }),
      );
    }
  }, [projects, colors]);

  const handleFilter = (): React.ReactNode => {
    if (user.user_type.type !== 'REP') {
      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <div style={{ width: 250 }}>
            <Select
              label="Estado"
              options={STATE_OPTIONS}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  state: e.value,
                  state_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Representante"
              options={representatives?.map(representative => ({
                value: representative.code,
                label:
                  representative.code === 'ALL'
                    ? 'Todos'
                    : `(${representative.code}) - ${representative.name}`,
              }))}
              value={getRepValueForSelect(
                filters.erp_rep_code,
                representatives,
              )}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_rep_code: e.value,
                  rep_refreshed: true,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Cliente"
              options={clients?.map(client => ({
                value: client.code,
                label:
                  client.code === 'ALL'
                    ? 'Todos'
                    : `(${client.code}) - ${client.company_name}`,
              }))}
              value={getClientValueForSelect(filters.erp_client_code, clients)}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  erp_client_code: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Status"
              options={[
                { value: 'Pendente', label: 'Pendente' },
                { value: 'Finalizado', label: 'Finalizado' },
                { value: 'Cancelado', label: 'Cancelado' },
                { value: 'ALL', label: 'Todos' },
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  status: e.value,
                  refresh: true,
                });
              }}
            />
          </div>

          <div style={{ width: 250 }}>
            <Select
              label="Ano"
              options={[
                { value: 'ALL', label: 'Todos' },
                ...Array.from(
                  { length: new Date().getFullYear() - 2021 + 1 },
                  (_, i) => new Date().getFullYear() - i,
                ).map(y => ({ value: String(y), label: String(y) })),
              ]}
              darkBorder
              onSet={(e: any) => {
                setFilters({
                  ...filters,
                  year: e.value,
                  refresh: true,
                });
              }}
            />
          </div>
        </div>
      );
    }

    return null;
  };

  return (
    <div style={{ maxWidth: 1080 }}>
      {handleFilter()}

      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          rowGap: 30,
          marginTop: 20,
        }}
      >
        {PROJECT_TYPES.map(proj_type => (
          <div>
            <div style={{ fontSize: '1.4rem', color: colors.labelInput }}>
              {proj_type.label}
            </div>

            <StackedColumns
              series={
                chartData.find(c_d => c_d.project_type === proj_type.name)
                  ?.series || []
              }
              categories={
                chartData.find(c_d => c_d.project_type === proj_type.name)
                  ?.categories || []
              }
              colors={
                chartData.find(c_d => c_d.project_type === proj_type.name)
                  ?.colors || []
              }
              width={
                180 +
                (chartData.find(c_d => c_d.project_type === proj_type.name)
                  ?.categories.length || 0) *
                  70
              }
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default Procedures;
